import React, { FC, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { ZSwitch } from 'src/components/widgets/zswitch';
import { T, backofficeUrl } from 'src/shared/helpers';
import type { AreaBackgroundRuleEditorProps } from '../common/types';
import { useToolDefinitions } from '../common/constants';
import type { areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery } from './__generated__/areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery.graphql';
import type { areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation } from './__generated__/areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation.graphql';
import type { areaBackgroundRuleEditor_AreaBackgroundRulesListQuery } from './__generated__/areaBackgroundRuleEditor_AreaBackgroundRulesListQuery.graphql';
import {
    StackedBreadcrumb,
    StackedBody,
    SectionCard,
    SectionCardTitle,
    FormSwitchRow,
    FormSwitchLabel,
    SharedWarningCallout,
    SettingHint,
    EmptyRulesCenter,
    InfoCallout
} from '../common/styled';

const areaBackgroundRulesListQuery = graphql`
    query areaBackgroundRuleEditor_AreaBackgroundRulesListQuery {
        areaBackgroundRules {
            id
            areaBackgroundRuleID
        }
    }
`;

const areaBackgroundRuleDetailQuery = graphql`
    query areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery($id: ID!) {
        areaBackgroundRule(id: $id) {
            id
            areaBackgroundRuleID
            name
            colorList
            enableNoColor
            enableImageCustomerGallery
            enableImageSellerGallery
            imageGalleryCustomerFormat
            productsSides {
                id
                productID
                sideID
            }
            printTypeIDs
        }
    }
`;

const AreaBackgroundRuleEditorDetail: FC<AreaBackgroundRuleEditorProps & { ruleId: string }> = ({ ruleId, productId, toolKey, onBack, onSave, saveRef }) => {
    const TOOL_DEFINITIONS = useToolDefinitions();
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query areaBackgroundRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery>(areaBackgroundRuleDetailQuery, { id: ruleId });
    const rule = json?.areaBackgroundRule;
    const ruleName = rule?.name || '';
    const assignedProductCount = rule?.productsSides ? new Set(rule.productsSides.map((productSide) => productSide.productID)).size : undefined;

    const ruleRef = useRef<any>(rule);
    const [enableColor, setEnableColor] = useState(() => Array.isArray(rule?.colorList) && rule.colorList.length > 0);
    const [enableUploadImage, setEnableUploadImage] = useState(() => rule?.enableImageCustomerGallery || false);
    const [enableImagesGallery, setEnableImagesGallery] = useState(() => rule?.enableImageSellerGallery || false);

    const [commitUpdateAreaBg] = useMutation<areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation>(graphql`
        mutation areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation($input: AreaBackgroundRuleToolInput!) {
            areaBackgroundRuleSave(ruleInput: $input) {
                id
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!ruleRef.current) return;
        try {
            const input: any = {
                id: ruleRef.current.id,
                name: ruleRef.current.name,
                colorList: enableColor ? (ruleRef.current.colorList?.length > 0 ? ruleRef.current.colorList : ['#000000']) : [],
                enableNoColor: ruleRef.current.enableNoColor || false,
                enableImageCustomerGallery: enableUploadImage,
                enableImageSellerGallery: enableImagesGallery,
                imageGalleryCustomerFormat: enableUploadImage ? 2 : ruleRef.current.imageGalleryCustomerFormat || 0,
                productsSides: (ruleRef.current.productsSides || []).map((productSide: any) => ({
                    id: productSide.id,
                    sideGlobalID: productSide.sideID ? btoa(`Side:${productSide.sideID}`) : null
                })),
                printTypeIDs: ruleRef.current.printTypeIDs || []
            };
            commitUpdateAreaBg({
                variables: { input },
                onCompleted: () => onSave(),
                onError: () => {
                    /* ignore */
                }
            });
        } catch {
            /* ignore */
        }
    }, [enableColor, enableUploadImage, enableImagesGallery, onSave, commitUpdateAreaBg]);

    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {assignedProductCount != null && assignedProductCount > 1 && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Fill options', 'Admin')}</SectionCardTitle>

                    <FormSwitchRow>
                        <FormSwitchLabel>{T._('Color', 'Admin')}</FormSwitchLabel>
                        <ZSwitch checked={enableColor} onChange={() => setEnableColor(!enableColor)} />
                    </FormSwitchRow>
                    {enableColor && <SettingHint>{T._('Color picker and palette settings are available in full mode.', 'Admin')}</SettingHint>}

                    <FormSwitchRow>
                        <FormSwitchLabel>{T._('Upload image', 'Admin')}</FormSwitchLabel>
                        <ZSwitch checked={enableUploadImage} onChange={() => setEnableUploadImage(!enableUploadImage)} />
                    </FormSwitchRow>

                    <FormSwitchRow>
                        <FormSwitchLabel>{T._('Images gallery', 'Admin')}</FormSwitchLabel>
                        <ZSwitch checked={enableImagesGallery} onChange={() => setEnableImagesGallery(!enableImagesGallery)} />
                    </FormSwitchRow>
                    {enableImagesGallery && (
                        <InfoCallout>
                            {T._('The images gallery is managed from the', 'Admin')}{' '}
                            <a href={backofficeUrl('/tools/area-background/background-gallery')} target='_blank' rel='noopener'>
                                {T._('Background Gallery', 'Admin')} &#8250;
                            </a>
                        </InfoCallout>
                    )}
                </SectionCard>
            </StackedBody>
        </>
    );
};

const AreaBackgroundRuleEditorList: FC<AreaBackgroundRuleEditorProps> = (props) => {
    const listJson = useLazyLoadQuery<areaBackgroundRuleEditor_AreaBackgroundRulesListQuery>(areaBackgroundRulesListQuery, {});
    const node = useMemo(
        () => (listJson?.areaBackgroundRules || []).find((node: any) => String(node.areaBackgroundRuleID) === props.itemId),
        [listJson, props.itemId]
    );

    if (!node?.id) {
        return (
            <>
                <StackedBreadcrumb>
                    <span onClick={props.onBack}>{T._('Product', 'Admin')}</span> / {T._('Rule', 'Admin')}
                </StackedBreadcrumb>
                <StackedBody>
                    <EmptyRulesCenter>{T._('Rule not found', 'Admin')}</EmptyRulesCenter>
                </StackedBody>
            </>
        );
    }

    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <AreaBackgroundRuleEditorDetail {...props} ruleId={node.id} />
        </Suspense>
    );
};

export const AreaBackgroundRuleEditor: FC<AreaBackgroundRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <AreaBackgroundRuleEditorList {...props} />
        </Suspense>
    );
};
